import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, Flame, Rocket, Check } from "lucide-react";
import { LiveRuns } from "@/components/LiveRuns";
import {
  Clock, ListTodo, Sparkles, Activity, ArrowUpRight, TrendingUp, Trophy,
  Users, Send, PenLine, ShieldAlert, HelpCircle, Eye,
} from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-zinc-200 rounded-lg shadow-sm ${className}`}>{children}</div>
);

const dot = { info: "bg-zinc-400", success: "bg-emerald-500", warning: "bg-gold", error: "bg-red-500" };

const URGENCY_BADGE = {
  due: "bg-orange-100 text-orange-600", task: "bg-amber-100 text-amber-700",
  waiting: "bg-blue-100 text-blue-600", stalled: "bg-zinc-200 text-zinc-600", gap: "bg-red-100 text-red-600",
};

function Standup({ navigate }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pursuing, setPursuing] = useState("");
  const load = (force) => {
    setBusy(true);
    const req = force ? api.post("/standup/refresh") : api.get("/standup");
    req.then((r) => setS(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; `load` is stable-by-intent
  useEffect(() => { load(false); }, []);

  const go = (i) => {
    if (i.otype === "deal") navigate(`/deals/${i.oid}`);
    else if (i.otype && i.oid) navigate(`/r/${i.otype}/${i.oid}`);
  };

  const pursue = async (opp_key, e) => {
    e.stopPropagation();
    setPursuing(opp_key);
    try {
      const r = await api.post(`/opportunities/${opp_key}/pursue`);
      toast.success(r.data.documents_attached ? `Pursuing — pulled ${r.data.documents_attached} doc(s).` : "Pursuing — added to pipeline.");
      navigate(`/deals/${r.data.deal_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't pursue that one.");
    } finally { setPursuing(""); }
  };

  const hot = s?.hot_opps || [];

  return (
    <Card className="p-5 mb-6 border-l-4 border-l-gold" >
      <div className="flex items-start justify-between gap-3" data-testid="standup-card">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-zinc-900 flex items-center justify-center"><AnvilIcon className="h-4 w-4 text-gold" /></div>
          <div>
            <div className="text-sm font-semibold text-zinc-900">Anvil's Daily Standup</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">{s?.date || "today"} · {s?.counts?.total ?? 0} items need eyes</div>
          </div>
        </div>
        <button onClick={() => load(true)} disabled={busy} data-testid="standup-refresh" className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </button>
      </div>
      <p className="text-sm text-zinc-700 leading-relaxed mt-3 whitespace-pre-line" data-testid="standup-text">
        {s ? s.text : "Anvil is scanning the board…"}
      </p>

      {hot.length > 0 && (
        <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50/60 p-3" data-testid="standup-hot-opps">
          <div className="flex items-center gap-1.5 mb-2">
            <Flame className="h-3.5 w-3.5 text-orange-600" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-orange-700">Closing this week · {hot.length}</span>
          </div>
          <div className="space-y-1.5">
            {hot.map((h, idx) => (
              <div key={h.opp_key || idx} data-testid={`standup-hot-${idx}`}
                onClick={() => navigate("/discover")}
                className="flex items-center justify-between gap-3 bg-white border border-orange-100 rounded-md px-3 py-2 hover:border-orange-300 transition-colors cursor-pointer">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{h.title}</div>
                  <div className="text-[11px] text-zinc-500 font-mono truncate">{[h.agency, h.naics && `NAICS ${h.naics}`].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-semibold text-orange-700 whitespace-nowrap">{h.due_label}</span>
                  <button onClick={(e) => pursue(h.opp_key, e)} disabled={pursuing === h.opp_key} data-testid={`standup-hot-pursue-${idx}`}
                    className="flex items-center gap-1 text-[11px] bg-emerald-600 text-white px-2.5 py-1 rounded-md hover:bg-emerald-500 disabled:opacity-50">
                    {pursuing === h.opp_key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />} Pursue
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {s?.items?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="standup-items">
          {s.items.slice(0, 12).map((i, idx) => (
            <button key={i.oid ? `${i.otype}-${i.oid}` : idx} onClick={() => go(i)} data-testid={`standup-item-${idx}`}
              className="flex items-center gap-1.5 text-[11px] border border-zinc-200 rounded-full pl-1.5 pr-2.5 py-1 hover:border-gold-dark transition-colors">
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${URGENCY_BADGE[i.kind] || "bg-zinc-100 text-zinc-600"}`}>{i.kind}</span>
              <span className="text-zinc-800 font-mono">{i.label}</span>
              <span className="text-zinc-400">{i.detail}</span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Home() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  const completeTask = async (t, e) => {
    e.stopPropagation();
    if (!t.id) return;
    setD((prev) => ({ ...prev, open_tasks: prev.open_tasks.filter((x) => x.id !== t.id) }));
    try {
      await api.put(`/records/tasks/${t.id}`, { status: "done" });
      toast.success("Task completed", {
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.put(`/records/tasks/${t.id}`, { status: "open" }); } catch { /* ignore */ }
            api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
          },
        },
      });
    } catch {
      toast.error("Couldn't complete task");
      api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch dashboard once on mount
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    // Anvil drops a quirky greeting / dumb compliment on arrival, throttled to ~every 90 min.
    let last = 0;
    try { last = Number(localStorage.getItem("anvil-greeted") || localStorage.getItem("bruh-greeted") || 0); } catch { /* ignore */ }
    if (Date.now() - last < 90 * 60 * 1000) return;
    api.get("/greeting").then((r) => {
      if (r.data?.greeting) {
        toast(r.data.greeting, { icon: <AnvilIcon className="h-4 w-4 text-gold" />, duration: 6000 });
        try { localStorage.setItem("anvil-greeted", String(Date.now())); } catch { /* ignore */ }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- greeting shows once on arrival only
  }, []);

  if (!d) return <div className="p-8 text-zinc-500 font-mono text-sm animate-pulse">Loading the field dashboard…</div>;

  return (
    <div className="p-5 sm:p-8 max-w-[1700px] mx-auto overflow-x-hidden" data-testid="home-dashboard">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900">Command</h1>
        <p className="text-sm text-zinc-500 mt-1">Every document. Every deal. Ironclad.</p>
      </div>

      <Standup navigate={navigate} />

      <LiveRuns />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={TrendingUp} label="Open deals" value={d.totals.open} accent="bg-blue-500" testid="stat-open" />
        <Stat icon={Trophy} label="Won" value={d.totals.won} accent="bg-emerald-500" testid="stat-won" />
        <Stat icon={Sparkles} label="Opportunities" value={d.opportunities.length} accent="bg-gold" testid="stat-opps" />
        <Stat icon={ListTodo} label="Open tasks" value={d.open_tasks.length} accent="bg-amber-500" testid="stat-tasks" />
      </div>

      <div className="mb-6"><WinLoss navigate={navigate} /></div>

      <div className="grid lg:grid-cols-2 2xl:grid-cols-4 gap-5 items-start">
        <Card className="p-5">
          <SectionTitle icon={Clock} title="Bids due soonest" />
          <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="due-soonest-list">
            {d.due_soonest.length === 0 && <Empty text="No upcoming due dates." />}
            {d.due_soonest.map((b) => (
              <Row key={b.id} testid={`due-${b.id}`} onClick={() => navigate(`/deals/${b.id}`)}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{b.title}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{b.est_code} · {b.bid_type}</div>
                </div>
                <div className="text-xs text-zinc-900 font-semibold whitespace-nowrap">{b.due_date}</div>
              </Row>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle icon={Sparkles} title="Opportunities to pursue / pass" />
          <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="opportunities-list">
            {d.opportunities.length === 0 && <Empty text="No new opportunities. Create one or discover in HigherGov." />}
            {d.opportunities.map((b) => (
              <Row key={b.id} testid={`opp-${b.id}`} onClick={() => navigate(`/deals/${b.id}`)}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{b.title}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{b.agency || b.bid_type} · {b.place_of_performance || "—"}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-400 shrink-0" />
              </Row>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle icon={ListTodo} title="Open tasks" />
          <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="open-tasks-list">
            {d.open_tasks.length === 0 && <Empty text="No open gaps or tasks. Everything compliant." />}
            {d.open_tasks.map((t, i) => (
              <Row key={i} testid={`task-${i}`} onClick={() => navigate(t.otype && t.otype !== "deal" ? `/r/${t.otype}/${t.oid}` : `/deals/${t.deal_id || t.oid}`)}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{t.task}</div>
                  <div className="text-[11px] text-zinc-500 font-mono truncate">{t.est_code}{t.assignee ? ` · ${t.assignee}` : ""}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.id && (
                    <button data-testid={`task-done-${i}`} onClick={(e) => completeTask(t, e)} title="Mark done"
                      className="h-5 w-5 rounded border border-zinc-300 hover:border-emerald-500 hover:bg-emerald-50 flex items-center justify-center text-transparent hover:text-emerald-600 transition-colors">
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${t.kind === "gap" ? "bg-red-100 text-red-600" : t.kind === "overdue" ? "bg-orange-100 text-orange-600" : "bg-amber-100 text-amber-700"}`}>{t.kind === "gap" ? "GAP" : t.kind === "overdue" ? "OVERDUE" : "TASK"}</span>
                </div>
              </Row>
            ))}
          </div>
        </Card>

        <UnifiedFeed navigate={navigate} />
      </div>
    </div>
  );
}

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const highIcon = (text = "", kind = "") => {
  const t = `${text} ${kind}`.toLowerCase();
  if (/(sent|submit|email)/.test(t)) return Send;
  if (/(sign|e-signature|esign|adopt)/.test(t)) return PenLine;
  if (/override/.test(t)) return ShieldAlert;
  if (/(re-?draft|re-?run|rerun)/.test(t)) return RefreshCw;
  return Activity;
};

function UnifiedFeed({ navigate, bidId = null, compact = false }) {
  const [items, setItems] = useState(null);
  const [signals, setSignals] = useState([]);
  const load = () => {
    api.get("/activity/team?limit=40").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    api.get("/signals?status=open").then((r) => setSignals(r.data.signals || [])).catch(() => setSignals([]));
  };
  useEffect(() => { load(); }, []);
  const requestHelp = async () => {
    const note = window.prompt("What do you need help with?");
    if (note === null) return;
    try { await api.post("/signals", { kind: "help", bid_id: bidId || "", note }); toast.success("Help requested — pinned to the Command feed."); load(); }
    catch { toast.error("Couldn't post the help request."); }
  };
  const resolve = async (sig) => {
    try { await api.post(`/signals/${sig.id}/resolve`); toast.success(sig.kind === "review" ? "Marked reviewed." : "Help resolved."); load(); }
    catch { toast.error("Couldn't update."); }
  };
  if (!items) return null;
  const open = (a) => { if (a.bid_id && a.bid_id !== "company" && a.bid_id !== "system") navigate(`/deals/${a.bid_id}`); };
  return (
    <Card className="p-5" data-testid="unified-activity-feed">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-zinc-900" />
          <h2 className="text-sm font-semibold text-zinc-900">Activity</h2>
        </div>
        <button onClick={requestHelp} data-testid="feed-request-help"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-full px-2.5 py-1 transition-colors">
          <HelpCircle className="h-3 w-3" /> Request help
        </button>
      </div>
      <div className="text-[11px] text-zinc-400 mb-3">Everyone can act — everyone sees who acted</div>

      {/* Pinned HELP / REVIEW signals */}
      {signals.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="pinned-signals">
          {signals.map((s) => {
            const help = s.kind === "help";
            return (
              <div key={s.id} data-testid={`signal-${s.id}`}
                className={`rounded-md border p-2.5 ${help ? "border-amber-300 bg-amber-50" : "border-sky-300 bg-sky-50"}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  {help ? <HelpCircle className="h-3.5 w-3.5 text-amber-600" /> : <Eye className="h-3.5 w-3.5 text-sky-600" />}
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${help ? "text-amber-700" : "text-sky-700"}`}>{help ? "Help needed" : "Review requested"}</span>
                  <button onClick={() => resolve(s)} data-testid={`signal-resolve-${s.id}`}
                    className="ml-auto text-[10px] font-semibold text-zinc-600 hover:text-zinc-900 underline">
                    {help ? "Resolve" : "Mark reviewed"}
                  </button>
                </div>
                {s.note && <div className="text-[12px] text-zinc-700">{s.note}</div>}
                <div className="text-[11px] text-zinc-400 font-mono mt-0.5">
                  {s.actor}{s.est_code ? ` · ${s.est_code}` : ""} · {relTime(s.created_at)}
                  {s.bid_id && s.bid_id !== "" && <button onClick={() => navigate(`/deals/${s.bid_id}`)} className="ml-1 underline hover:text-zinc-600">open deal →</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={`space-y-1.5 overflow-y-auto pr-1 ${compact ? "max-h-[320px]" : "max-h-[440px]"}`} data-testid="activity-feed">
        {items.length === 0 && <Empty text="No recent activity." />}
        {items.map((a, i) => {
          const Icon = a.high ? highIcon(a.text, a.kind) : null;
          const linkable = a.bid_id && a.bid_id !== "company" && a.bid_id !== "system";
          return (
            <div key={i} data-testid={`activity-item-${i}`} onClick={() => open(a)}
              className={`flex items-start gap-2.5 rounded-md -mx-1 px-1.5 py-1.5 ${linkable ? "cursor-pointer hover:bg-zinc-50" : ""} ${a.high ? "bg-gold/[0.05]" : ""}`}>
              <span className="mt-0.5 h-6 w-6 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{initials(a.actor)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-zinc-900 truncate">{a.actor}</span>
                  {Icon && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-gold-dark bg-gold/20 rounded px-1.5 py-0.5" data-testid={`activity-high-${i}`}>
                      <Icon className="h-3 w-3" /> Key
                    </span>
                  )}
                </div>
                <div className={`text-[13px] leading-snug ${a.high ? "text-zinc-900 font-medium" : "text-zinc-700"}`}>{a.text}</div>
                <div className="text-[11px] text-zinc-400 font-mono truncate">{a.est_code || a.deal_title || "—"} · {relTime(a.at)}{linkable ? " · open deal →" : ""}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export { UnifiedFeed };

const Stat = ({ icon: Icon, label, value, accent = "bg-zinc-900", testid }) => (
  <div className="relative bg-white border border-zinc-200 rounded-lg shadow-sm p-4 overflow-hidden" data-testid={testid}>
    <span className={`absolute left-0 top-0 h-full w-1 ${accent}`} />
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
      <Icon className="h-4 w-4 text-zinc-900" />
    </div>
    <div className="font-display font-bold text-2xl text-zinc-900 mt-1">{value}</div>
  </div>
);

const RateBadge = ({ won, n, min = 3 }) => (
  n < min ? <span className="text-[11px] text-zinc-400">not enough data yet</span>
    : <span className="text-sm font-semibold text-zinc-900">{Math.round(100 * won / n)}% <span className="text-[11px] font-normal text-zinc-500">— {won} of {n}</span></span>
);

const WinLoss = () => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics/pipeline").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return null;
  const min = a.min_sample || 3;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg shadow-sm" data-testid="winloss-card">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <span className="text-[11px] tracking-[0.1em] font-semibold text-zinc-500 uppercase">Win / Loss & No-Bid</span>
        <span className="text-[11px] text-zinc-400">bid {a.counts.bid} · no-bid {a.counts.no_bid}</span>
      </div>
      <div className="p-4 grid md:grid-cols-3 gap-5 max-h-[340px] overflow-y-auto">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Overall win rate</div>
          <RateBadge won={a.overall.won} n={a.overall.n} min={min} />
          <div className="mt-3 space-y-1.5" data-testid="winrate-by-type">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">By solicitation type</div>
            {a.by_type.length === 0 && <div className="text-[11px] text-zinc-400">—</div>}
            {a.by_type.map((t) => (
              <div key={t.name} className="flex items-center justify-between text-xs"><span className="text-zinc-700">{t.name}</span><RateBadge won={t.won} n={t.n} min={min} /></div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">By agency</div>
          {a.by_agency.length === 0 && <div className="text-[11px] text-zinc-400">—</div>}
          {a.by_agency.map((t) => (
            <div key={t.name} className="flex items-center justify-between text-xs py-0.5"><span className="text-zinc-700 truncate max-w-[150px]">{t.name}</span><RateBadge won={t.won} n={t.n} min={min} /></div>
          ))}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Top reasons</div>
          <div className="text-[11px] text-zinc-500 mt-1">Passed (no-bid)</div>
          {a.pass_reasons.length === 0 ? <div className="text-[11px] text-zinc-400">—</div> : a.pass_reasons.map((r, i) => (
            <div key={i} className="flex justify-between text-xs"><span className="text-zinc-600 truncate max-w-[150px]">{r.reason}</span><span className="text-zinc-400">{r.count}</span></div>
          ))}
          <div className="text-[11px] text-zinc-500 mt-2">Lost</div>
          {a.loss_reasons.length === 0 ? <div className="text-[11px] text-zinc-400">—</div> : a.loss_reasons.map((r, i) => (
            <div key={i} className="flex justify-between text-xs"><span className="text-zinc-600 truncate max-w-[150px]">{r.reason}</span><span className="text-zinc-400">{r.count}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
};
const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-zinc-900" /><h2 className="text-sm font-semibold text-zinc-900">{title}</h2></div>
);
const Row = ({ children, onClick, testid }) => (
  <div onClick={onClick} data-testid={testid} role="button" tabIndex={0}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 transition-colors text-left cursor-pointer">
    {children}
  </div>
);
const Empty = ({ text }) => <div className="text-xs text-zinc-400 py-3">{text}</div>;
